import React, { useContext, useEffect, useState } from "react";
import { Button, Card, Table } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, WarningOutlined, EyeOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { getLowStock } from "../../../../network/network";
import { AppContext } from "../../../../context/AppContextProvider";
import LowStockOverviewModal from "./LowStockOverviewModal";
import { handleApiError } from "../../../../utils/errorHandler";

const LowStock = () => {
  const { appConfig } = useContext(AppContext);

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lowStockData, setLowStockData] = useState([]);
  const { t } = useTranslation();

  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  useEffect(() => {
    setIsDataLoading(true);

    getLowStock({
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const data = response.data?.data || {};
        const stock = data.Stock || [];
        const pagination = data.pagination || {};

        setLowStockData(stock);
        setPaginationData(pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [currentPage, perPageSize, t]);

  const showStockOverview = (row) => {
    if (row.product) {
      setSelectedProduct(row.product);
      setModalVisible(true);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedProduct(null);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "inventory",
      breadcrumbName: t("inventory"),
      icon: <WarningOutlined />,
    },
  ];

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  const tableColumns = [
    {
      title: t("image"),
      width: "85px",
      render: (record) => {
        return (
          record.product && (
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          )
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          record.product &&
          (appConfig.direction === "ltr"
            ? record.product.name_en
            : record.product.name)
        );
      },
    },
    {
      title: t("low_stock"),
      align: "center",
      dataIndex: "low_stock_quantity",
      render: (low_stock_quantity) => {
        return (
          <div style={{ color: "var(--color-danger)" }}>
            {low_stock_quantity}
          </div>
        );
      },
    },
    {
      title: t("branch_stock"),
      align: "center",
      render: (_, record) => {
        const qty = record?.branch?.current_quantity ?? 0;

        return (
          <div
            style={{
              color: qty > 0 ? "var(--color-success)" : "var(--color-danger)",
            }}
          >
            {qty}
          </div>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => (
        <Button type="link" onClick={() => showStockOverview(record)}>
          <EyeOutlined title={t("view_details")} />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader className="app-p-0" title={t("low_stock")} />
      </Card>

      <Card>
        <Table
          columns={tableColumns}
          dataSource={lowStockData.map((item, index) => ({
            ...item,
            key: item.id || index,
          }))}
          loading={isDataLoading}
          pagination={
            paginationData.total < 10
              ? false
              : {
                  current: paginationData.current_page || currentPage,
                  pageSize: paginationData.per_page || perPageSize,
                  total: paginationData.total || 0,
                  showSizeChanger: true,
                }
          }
          onChange={handleTableChange}
        />
      </Card>

      <LowStockOverviewModal
        visible={modalVisible}
        product={selectedProduct}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default LowStock;
